import React, { useCallback, useEffect, useRef, useState } from "react";
import { Elements, useStripe, useElements, CardNumberElement } from "@stripe/react-stripe-js";
import posthog from "posthog-js";
import { CB_EVENTS, CB_PAYMENT_FAILURE, CB_PAYMENT_METHOD } from "campaign-builder/analytics/cbAnalytics";
import { useCountryStripe } from "campaign-builder/checkout/useStripe";
import { useCheckoutPayment } from "./useCheckoutPayment";
import { useCardInput } from "./useCardInput";
import {
  CARD_NOT_SAVEABLE_HELPERS,
  ERROR_CODE_CARD_NOT_SAVEABLE,
  isSaveableFunding,
  useCardFunding,
  type NotSaveableFunding,
} from "./useCardFunding";
import { CardBrandIcon } from "./CardBrandIcon";
import { SavedCardSelector } from "./SavedCardSelector";
import { NewCardForm, AddCardFormData } from "./NewCardForm";
import { CheckoutSummary } from "./CheckoutSummary";
import { Skeleton } from "campaign-builder/ui";
import {
  createSetupIntent,
  addPaymentMethod,
  createTeamCardSetupIntent,
  addTeamCard,
  initiateCharge,
  verifyCharge,
} from "./billingApi";
import {
  fromCustomAddCardAddress,
  fromDefaultBillingAddress,
  toStripeBillingDetails,
  type NormalizedAddress,
} from "./toStripeBillingDetails";
import { toCardBrand, type CostBreakdown } from "./types";
import { Button } from "components/ui/button";
import { Loader2 } from "lucide-react";
import { useBillingGateSurface } from "./billing-profile/useBillingGateSurface";

// ── Types ──────────────────────────────────────────────────────────────────

type CheckoutView = "payment" | "selector" | "addCard" | "processing" | "threeds" | "success" | "failed";

interface CheckoutContentProps {
  campaignId: string;
  clientId: string;
  /**
   * Null while the server-authoritative breakdown is
   * loading or stale (CheckoutSummary renders a skeleton). Non-null is
   * the canonical server-computed breakdown — never an FE
   * approximation. The Pay button is disabled while null OR
   * `breakdownReady` is false.
   */
  breakdown: CostBreakdown | null;
  /**
   * True only when the server response matches the current
   * {subtotal, currency, couponCode, campaignId}. False when the user
   * has just changed inputs and a fresh preview hasn't landed yet.
   */
  breakdownReady?: boolean;
  /** Stripe promotion code applied to the charge, if any. */
  couponCode?: string;
  /**
   * HUB-498: apply the client's active review-discount entitlement to the
   * charge (server-resolved). Wins over `couponCode` — one discount per order.
   */
  applyReviewDiscount?: boolean;
  /**
   * Optional. When missing, the wrapper short-circuits and renders a
   * disabled state instead of mounting Stripe Elements with a wrong /
   * default key — previously defaulted to "US" at the caller
   * and caused cross-account 404s on Stripe.js confirmations for
   * non-US clients.
   */
  countryCode?: string;
  onClose: () => void;
  onPaymentSuccess?: () => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount);
}

// Shared style tokens — match the sibling OrderDetailsDrawer (shadcn/Tailwind).
// Primary CTA uses the Oppizi red #ef2b55 like the sibling's "Go to payment".
const CHANGE_CARD_BTN =
  "flex items-center gap-1 rounded-lg p-2 text-sm font-medium text-foreground hover:bg-slate-50 dark:hover:bg-accent";
const ERROR_BANNER = "rounded-lg bg-destructive/10 px-3 py-3 text-sm leading-5 text-destructive";
// Amber, like BillingProfileGateBanner's warning tone: something changed that isn't an error.
const NOTICE_BANNER =
  "rounded-lg border border-solid border-[#fee685] bg-[#fffbeb] px-3 py-3 text-sm leading-5 text-[#973c00] dark:border-[#973c00] dark:bg-[#461901] dark:text-[#fef3c6]";
const LOADING_TEXT = "text-muted-foreground flex items-center justify-center py-10 text-base";
// Centered status views (processing / 3DS / success / failed)
const CENTER_STATE = "flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center";
const CENTER_ICON = "flex h-10 w-10 items-center justify-center";
// Centered status hero (success/failed/processing) — focal by position, one
// step above the small top-left view header.
const CENTER_TITLE = "text-foreground m-0 text-xl font-semibold leading-7";
const CENTER_DESC = "text-muted-foreground max-w-[360px] whitespace-pre-line text-base font-medium leading-6";
const CENTER_BUTTONS = "mt-2 flex w-60 flex-col gap-3";
// Shared header/chrome (used across every view)
// [&_*]:box-border — the checkout isn't inside an sds-scope, so Tailwind's
// border-box preflight doesn't apply here; without it, `h-*` + the shadcn
// Button's `py-2`/border stack (e.g. h-11 → 62px). Force border-box on every
// descendant so heights are exact and w-full never overflows.
const CONTENT_ROOT = "flex h-full min-h-screen flex-col gap-4 overflow-y-auto p-8 [&_*]:box-border";
const HEADER = "flex items-center justify-between gap-2";
// L1 view title — matches the sibling's SheetTitle (text-lg font-semibold).
const HEADER_TITLE = "text-foreground m-0 text-lg font-semibold leading-7";
const CLOSE_BTN = "text-muted-foreground hover:text-foreground flex items-center justify-center p-1";
const BACK_BTN = "text-muted-foreground hover:text-foreground flex items-center justify-center p-1";

// ── Inline Icons ───────────────────────────────────────────────────────────

function ArrowLeftIcon(): React.ReactElement {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CloseIcon(): React.ReactElement {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronRightIcon(): React.ReactElement {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Stripe generic card placeholder (gray card with "135") */
function StripeCardPlaceholder(): React.ReactElement {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="4" y="10" width="32" height="20" rx="3" fill="#E2E2FC" stroke="#AEAFC7" strokeWidth="0.8" />
      <rect x="4" y="16" width="32" height="4" fill="#AEAFC7" />
      <rect x="7" y="23" width="14" height="3" rx="1" fill="#fff" />
      <text x="9" y="25.5" fontSize="4" fontFamily="sans-serif" fill="#626379" fontWeight="600">
        135
      </text>
    </svg>
  );
}

/** Stripe wordmark logo */
function StripeLogo(): React.ReactElement {
  return (
    <svg width="33" height="14" viewBox="0 0 33 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M32.227 7.249c0-2.407-1.163-4.307-3.388-4.307-2.236 0-3.585 1.9-3.585 4.29 0 2.832 1.595 4.258 3.882 4.258 1.116 0 1.96-.253 2.597-.61V8.78c-.637-.319-1.368-.517-2.293-.517-.908 0-1.713.319-1.816 1.42h4.577c0-.12.026-.604.026-1.033v-.401ZM27.6 6.2c0-1.057.645-1.497 1.233-1.497.57 0 1.18.44 1.18 1.497H27.6Zm-5.206-3.258c-.918 0-1.507.43-1.835.729l-.122-.578h-2.06v11.25l2.34-.496.007-2.73c.34.246.84.594 1.67.594 1.687 0 3.225-1.357 3.225-4.346-.012-2.734-1.563-4.423-3.225-4.423Zm-.567 6.8c-.557 0-.885-.198-1.112-.44l-.013-3.476c.24-.27.58-.455 1.125-.455.86 0 1.456.964 1.456 2.18 0 1.25-.583 2.19-1.456 2.19ZM16.29 2.62h2.352v8.73H16.29V2.62Zm0-2.483L18.642 0v1.833H16.29V.137ZM13.94 3.505l-.147-.563h-2.028v8.408h2.34V5.68c.553-.721 1.49-.593 1.782-.49V2.942c-.304-.114-1.414-.323-1.948.563ZM9.35 1.038l-2.282.486-.007 7.7c0 1.422 1.068 2.47 2.49 2.47.788 0 1.365-.145 1.683-.317V9.357c-.305.124-1.81.562-1.81-.848v-3.4h1.81V2.942H9.424l-.074-1.904ZM2.597 5.38c0-.365.302-.504.797-.504.714 0 1.614.216 2.328.6V3.295A6.2 6.2 0 0 0 3.394 2.94C1.378 2.94 0 4.043 0 5.738c0 2.58 3.552 2.168 3.552 3.28 0 .43-.374.571-.898.571-.776 0-1.77-.32-2.556-.751v2.213c.87.376 1.752.537 2.556.537 2.06 0 3.478-1.019 3.478-2.73-.012-2.784-3.535-2.289-3.535-3.478Z"
        fill="rgba(34,35,54,0.6)"
      />
    </svg>
  );
}

function SuccessCircleIcon(): React.ReactElement {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="20" r="18" fill="#4CAF50" />
      <path d="M13 20L18 25L27 15" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FailedCircleIcon(): React.ReactElement {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="20" r="18" fill="#D32F2F" />
      <path d="M15 15L25 25" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M25 15L15 25" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function CheckmarkIcon(): React.ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5 10L9 14L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Footer (shared across all screens) ─────────────────────────────────────

function DrawerFooter(): React.ReactElement {
  return (
    <div className="flex h-8 items-center justify-center gap-[15px] py-2">
      <span className="text-muted-foreground flex items-center gap-1 text-xs font-normal leading-4">
        Powered by <StripeLogo />
      </span>
      <div className="h-4 w-px bg-border" />
      <div className="flex items-center gap-4">
        <span className="text-muted-foreground hover:text-foreground cursor-pointer text-xs font-normal">Legal</span>
        <span className="text-muted-foreground hover:text-foreground cursor-pointer text-xs font-normal">Returns</span>
        <span className="text-muted-foreground hover:text-foreground cursor-pointer text-xs font-normal">Contact</span>
      </div>
    </div>
  );
}

// ── Toast ───────────────────────────────────────────────────────────────────

interface ToastProps {
  message: string;
  onClose: () => void;
}

function Toast({ message, onClose }: ToastProps): React.ReactElement {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-lg bg-[#2e7d32] px-4 py-3 text-sm text-white shadow-lg">
      <span className="h-5 w-5 shrink-0">
        <CheckmarkIcon />
      </span>
      {message}
      <button type="button" className="ml-2 flex items-center justify-center p-0.5 text-white" onClick={onClose}>
        <CloseIcon />
      </button>
    </div>
  );
}

// ── Main Inner Component ───────────────────────────────────────────────────

const CheckoutContentInner: React.FC<CheckoutContentProps> = ({
  campaignId,
  clientId,
  breakdown,
  breakdownReady = true,
  couponCode,
  applyReviewDiscount,
  countryCode,
  onClose,
  onPaymentSuccess,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const paymentInProgressRef = useRef(false);
  const pollCancelledRef = useRef(false);
  const stripe = useStripe();
  const elements = useElements();
  const checkout = useCheckoutPayment();
  const cardInput = useCardInput();
  const cardFunding = useCardFunding(cardInput);
  const { markNotSaveable } = cardFunding;
  const [view, setView] = useState<CheckoutView>("payment");
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [addCardError, setAddCardError] = useState<string | null>(null);
  const [isAddingCard, setIsAddingCard] = useState(false);
  const addCardRef = useRef(false);
  // Kept in a ref so the success-redirect effect below fires exactly once (the
  // parent passes a fresh inline callback each render).
  const onPaymentSuccessRef = useRef(onPaymentSuccess);
  onPaymentSuccessRef.current = onPaymentSuccess;

  // Dynamic processing status text
  const [processingTitle, setProcessingTitle] = useState("Processing payment...");
  const [processingDescription, setProcessingDescription] = useState(
    "We're securely processing your payment through Stripe."
  );
  const [paymentError, setPaymentError] = useState<string | null>(null);
  // Billing-profile gate: an Enterprise/Agency team with no profile cannot pay;
  // the server refuses the charge too, so this is a UX layer over that backstop.
  const billingGate = useBillingGateSurface(clientId);
  const billingBlocked = billingGate.blocked;

  // Tracks a newly entered card that isn't persisted yet.
  // Used when saveCard=false: card is used for this checkout only.
  // Stash the resolved billing address too so confirmCardPayment can attach it for AVS.
  const [pendingCard, setPendingCard] = useState<{
    paymentMethodId: string;
    brand: string;
    last4: string;
    funding: string | null;
    billingAddress: NormalizedAddress | null;
  } | null>(null);

  // Override CheckoutSidebar's width and hide its native close button.
  // The sidebar renders: content wrapper > [close button, content div].
  // We walk up from our root to find the sidebar panel and apply overrides.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    // DOM structure: rootRef → content div (parent) → sidebar panel (grandparent)
    // We walk structurally instead of matching class names because MUI minifies
    // class names in production builds (e.g. "jss42" instead of "makeStyles-sidebar-42").
    const contentDiv = el.parentElement;
    const sidebarPanel = contentDiv?.parentElement;
    if (sidebarPanel) {
      sidebarPanel.style.width = "600px";
      // Hide the sidebar's native close button (first child button)
      const nativeClose = sidebarPanel.querySelector(":scope > button") as HTMLElement | null;
      if (nativeClose) nativeClose.style.display = "none";
    }
    if (contentDiv) {
      contentDiv.style.padding = "0";
    }
    return () => {
      pollCancelledRef.current = true;
      if (sidebarPanel) {
        sidebarPanel.style.width = "";
        const nativeClose = sidebarPanel.querySelector(":scope > button") as HTMLElement | null;
        if (nativeClose) nativeClose.style.display = "";
      }
      if (contentDiv) {
        contentDiv.style.padding = "";
      }
    };
  }, []);

  // Payment success is terminal (PAD-31453): the success screen has no close or
  // finish control, and after a short beat — long enough to read the confirmation
  // — the parent redirects the client out of the builder, so they can't
  // return to the paid campaign and pay again.
  useEffect(() => {
    if (view !== "success") return;
    const timer = window.setTimeout(() => onPaymentSuccessRef.current?.(), 3500);
    return () => window.clearTimeout(timer);
  }, [view]);

  const {
    paymentMethods,
    defaultBillingAddress,
    selectedPaymentMethodId,
    selectPaymentMethod,
    saveCard,
    setSaveCard,
    isLoading,
    error,
    selectedCard,
    isMasterAccount,
    accountResolved,
    canAddMore,
  } = checkout;

  const hasCards = paymentMethods.length > 0;
  const hasSelectedCard = pendingCard !== null || (selectedCard !== null && !selectedCard.isExpired);

  // Only credit and debit cards can be saved (PAD-32618). Any other card unticks "Save card", says why under the
  // checkbox, and pays for this checkout only.
  const saveCardBlockedReason =
    cardFunding.funding !== null && !isSaveableFunding(cardFunding.funding)
      ? CARD_NOT_SAVEABLE_HELPERS[cardFunding.funding as NotSaveableFunding]
      : null;
  const saveCardBlocked = saveCardBlockedReason !== null;
  // Set when the server refused a save the Stripe check had missed, so the client knows why Confirm stopped.
  const [addCardNotice, setAddCardNotice] = useState<string | null>(null);
  const autoUntickedSaveCardRef = useRef(false);
  useEffect(() => {
    if (saveCardBlocked && saveCard) {
      autoUntickedSaveCardRef.current = true;
      setSaveCard(false);
    } else if (!saveCardBlocked && autoUntickedSaveCardRef.current) {
      // Restore only the tick we removed; a client's own untick stays.
      autoUntickedSaveCardRef.current = false;
      setSaveCard(true);
    }
    if (!saveCardBlocked) setAddCardNotice(null); // the card changed, so the notice no longer applies
  }, [saveCardBlocked, saveCard, setSaveCard]);

  const handleChangeCard = useCallback(() => {
    setView("selector");
  }, []);

  const handleSelectCard = useCallback(
    (id: number | "new") => {
      if (id === "new" && !canAddMore) return;
      selectPaymentMethod(id);
      if (id === "new") {
        cardInput.reset();
        setView("addCard");
      } else {
        setPendingCard(null);
        setView("payment");
      }
    },
    [selectPaymentMethod, canAddMore]
  );

  const handleAddCardFromSelector = useCallback(() => {
    if (!canAddMore) return;
    cardInput.reset();
    setView("addCard");
  }, [cardInput, canAddMore]);

  const handleBackToPayment = useCallback(() => {
    if (!pendingCard && hasCards) {
      const primary = paymentMethods.find((m) => m.isPrimary && !m.isExpired);
      const firstValid = paymentMethods.find((m) => !m.isExpired);
      const fallback = primary || firstValid;
      if (fallback) {
        selectPaymentMethod(fallback.id);
      }
    }
    setView("payment");
  }, [pendingCard, hasCards, paymentMethods, selectPaymentMethod]);

  const handleConfirmPayment = useCallback(async () => {
    if (!hasSelectedCard || !stripe || paymentInProgressRef.current) return;

    // Belt-and-suspenders breakdown guard. Pay button is already gated
    // on `breakdownReady && breakdown`, but a programmatic call could
    // bypass the disabled state. Both conditions matter:
    //   - `!breakdown`: drawer is showing the skeleton, no numbers to
    //     send.
    //   - `!breakdownReady`: drawer is showing a STALE breakdown (e.g.
    //     user changed coupon and the new preview is still in flight).
    //     Submitting now would charge with the new couponCode but the
    //     displayed total reflects the old one — same display/charge
    //     mismatch the fingerprint check is meant to prevent.
    // Place this BEFORE any state mutations — placing it after
    // `setView("processing")` would lock the drawer in the processing
    // screen forever (the `finally` block that resets
    // `paymentInProgressRef` would never run).
    if (!breakdown || !breakdownReady) {
      return;
    }

    paymentInProgressRef.current = true;
    pollCancelledRef.current = false;

    const POLL_INTERVAL_MS = 2000;
    const POLL_TIMEOUT_MS = 300000;

    setPaymentError(null);
    setProcessingTitle("Preparing your payment...");
    setProcessingDescription("Setting up a secure connection with Stripe.");
    setView("processing");

    try {
      // Step 1: Initiate charge — server-authoritative shape.
      // Backend computes fee + tax + total from {subtotalCents, couponCode |
      // applyReviewDiscount} and currency. The displayed `breakdown` prop is
      // what the user sees, but the actual PI amount is whatever backend
      // recomputes — so the review discount (HUB-498) must be signalled here,
      // not just reflected in the displayed breakdown.
      const subtotalCents = Math.round(breakdown.subtotal * 100);
      const chargePayload = {
        entityId: String(campaignId),
        entityType: "CAMPAIGN" as const,
        paymentMethodId: selectedCard ? selectedCard.id : null,
        // Never ask to save a card the API would refuse: the box can be re-ticked after the card was tokenized.
        saveCard: pendingCard ? saveCard && isSaveableFunding(pendingCard.funding) : false,
        subtotalCents,
        currency: breakdown.currency.toUpperCase(),
        // One discount per order: review wins; coupon is only sent otherwise.
        ...(applyReviewDiscount ? { applyReviewDiscount: true } : couponCode ? { couponCode } : {}),
      };

      setProcessingTitle("Initiating payment...");
      setProcessingDescription("Creating your payment request.");

      const { clientSecret, chargeUuid } = await initiateCharge(chargePayload);

      // Step 2: Confirm with Stripe (may open 3DS window)
      setView("threeds");

      let confirmResult;
      if (selectedCard) {
        // Saved card — payment method already attached by backend
        confirmResult = await stripe.confirmCardPayment(clientSecret);
      } else if (pendingCard) {
        // New unsaved card — reuse the PaymentMethod tokenized at add-time
        confirmResult = await stripe.confirmCardPayment(clientSecret, {
          payment_method: pendingCard.paymentMethodId,
        });
      } else {
        throw new Error("No payment method selected");
      }

      if (confirmResult.error) {
        throw new Error(confirmResult.error.message || "Payment confirmation failed");
      }

      // Step 3: Poll verify until paid/failed
      setView("processing");
      setProcessingTitle("Verifying payment...");
      setProcessingDescription("Confirming your payment was received.");

      const startTime = Date.now();

      const pollVerify = async (): Promise<"paid" | "failed"> => {
        if (pollCancelledRef.current) {
          throw new Error("Payment verification cancelled");
        }

        const result = await verifyCharge(chargeUuid);

        if (result.status === "authorized" || result.status === "paid" || result.status === "failed") {
          return result.status;
        }

        if (Date.now() - startTime > POLL_TIMEOUT_MS) {
          throw new Error("Payment verification timed out. Please check your billing page for the result.");
        }

        setProcessingTitle("Almost there...");
        setProcessingDescription("Waiting for payment confirmation from your bank.");

        return new Promise((resolve, reject) => {
          setTimeout(() => {
            if (pollCancelledRef.current) {
              reject(new Error("Payment verification cancelled"));
              return;
            }
            pollVerify().then(resolve).catch(reject);
          }, POLL_INTERVAL_MS);
        });
      };

      const finalStatus = await pollVerify();

      if (pollCancelledRef.current) return;

      if (finalStatus === "authorized" || finalStatus === "paid") {
        // PAD-32157 — payment success. Channel comes from the session-wide
        // register() call at the builder entry point. `amount` is in the local
        // currency (CAD/AUD/NZD/EUR/GBP/CHF for non-US clients); do NOT assume
        // USD — downstream analytics must convert using the `currency` property.
        posthog.capture(CB_EVENTS.PAYMENT_CONFIRMED, {
          campaign_id: campaignId,
          amount: breakdown.total,
          currency: breakdown.currency,
          payment_method: selectedCard ? CB_PAYMENT_METHOD.SAVED_CARD : CB_PAYMENT_METHOD.NEW_CARD,
        });
        setView("success");
      } else {
        posthog.capture(CB_EVENTS.PAYMENT_FAILED, {
          campaign_id: campaignId,
          failure_reason: CB_PAYMENT_FAILURE.CARD_DECLINED,
        });
        setPaymentError("Your payment was declined by your bank.");
        setView("failed");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Payment failed";
      // If the server's profile gate refused the charge, surface the banner and
      // open setup rather than the generic failure view.
      if ((err as { code?: string })?.code === "BILLING_PROFILE_REQUIRED") {
        if (!pollCancelledRef.current) {
          billingGate.openSetup();
        }
      } else if (!pollCancelledRef.current) {
        // Standardized failure token — never emit raw backend error text as a
        // property value (fragments analytics into free-text and risks leaking
        // internal error details into PostHog).
        posthog.capture(CB_EVENTS.PAYMENT_FAILED, {
          campaign_id: campaignId,
          failure_reason: CB_PAYMENT_FAILURE.UNKNOWN,
        });
        setPaymentError(msg);
        setView("failed");
      }
    } finally {
      paymentInProgressRef.current = false;
    }
  }, [
    hasSelectedCard,
    stripe,
    elements,
    breakdown,
    campaignId,
    selectedCard,
    pendingCard,
    saveCard,
    cardInput,
    billingGate,
  ]);

  const handleAddCardConfirm = useCallback(
    async (formData: AddCardFormData) => {
      if (!cardInput.validateFields()) return;
      if (addCardRef.current) return;
      if (!accountResolved) {
        setAddCardError("Could not load your account. Please try again.");
        return;
      }
      if (!stripe || !elements) {
        setAddCardError("Stripe has not loaded yet");
        return;
      }

      const cardNumberElement = elements.getElement(CardNumberElement);
      if (!cardNumberElement) {
        setAddCardError("Card number field not found");
        return;
      }

      addCardRef.current = true;
      setIsAddingCard(true);
      setAddCardError(null);

      // Resolve the address to attach to the Stripe PaymentMethod. PCI scope
      // keeps this on Stripe — our DB only stores the *default* invoice address.
      const addressForStripe = formData.billingAddress
        ? fromCustomAddCardAddress(formData.billingAddress)
        : defaultBillingAddress
        ? fromDefaultBillingAddress(defaultBillingAddress)
        : null;

      try {
        // No "Set as default Billing Address" write here: `PUT /billing/address`
        // no longer exists on oppizi-api (the account default is the billing
        // profile / Xero mirror now), so a card's address only travels to Stripe
        // in billing_details below (PAD-32909).
        if (saveCard) {
          // Full setup flow: create setup intent → confirm with Stripe → save card.
          // A Master Account team saves onto its own Stripe customer.
          const { clientSecret } = isMasterAccount ? await createTeamCardSetupIntent() : await createSetupIntent();

          const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
            payment_method: {
              card: cardNumberElement,
              billing_details: toStripeBillingDetails(cardInput.cardholderName.trim(), addressForStripe),
            },
          });

          if (stripeError) {
            setAddCardError(stripeError.message ?? "Card setup failed");
            return;
          }

          if (!setupIntent?.payment_method) {
            setAddCardError("Failed to set up payment method");
            return;
          }

          const stripePaymentMethodId =
            typeof setupIntent.payment_method === "string" ? setupIntent.payment_method : setupIntent.payment_method.id;

          if (isMasterAccount) {
            // The server makes the team's first card Primary (AC183).
            await addTeamCard({ stripePaymentMethodId, setAsPrimary: false, sharedWithTeam: true });
          } else {
            // Determine if this is the first card (auto-set as primary)
            const isFirstCard = paymentMethods.length === 0;
            await addPaymentMethod({
              stripePaymentMethodId,
              setAsPrimary: isFirstCard,
            });
          }

          // Refresh payment methods and select the newly added card
          const updatedMethods = await checkout.refetch();
          const newestCard = [...updatedMethods].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )[0];
          if (newestCard) {
            selectPaymentMethod(newestCard.id);
          }

          setPendingCard(null);
          setToastMessage("Card added successfully!");
          setView("payment");
        } else {
          // saveCard=false: card is for this transaction only. Tokenize now
          // while the card element is mounted in this view and reuse the
          // PaymentMethod at confirm, so the card survives the switch to the
          // payment view without keeping the element alive.
          const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
            type: "card",
            card: cardNumberElement,
            billing_details: toStripeBillingDetails(cardInput.cardholderName.trim(), addressForStripe),
          });
          if (pmError || !paymentMethod) {
            setAddCardError(pmError?.message ?? "Could not validate card");
            return;
          }
          setPendingCard({
            paymentMethodId: paymentMethod.id,
            brand: paymentMethod.card?.brand ?? "unknown",
            last4: paymentMethod.card?.last4 ?? "••••",
            funding: paymentMethod.card?.funding ?? null,
            billingAddress: addressForStripe,
          });
          selectPaymentMethod("new");
          setToastMessage("Card ready for payment");
          setView("payment");
        }
      } catch (err) {
        const { code, funding } = (err as { code?: string; funding?: string }) ?? {};
        const msg = err instanceof Error ? err.message : "Failed to add card";
        if (code === ERROR_CODE_CARD_NOT_SAVEABLE) {
          // The Stripe check missed it. Saving is optional here: lock "Save card", say so, and let Confirm pay without it.
          const recorded = markNotSaveable(funding);
          setAddCardNotice(
            `${CARD_NOT_SAVEABLE_HELPERS[recorded]} "Save card for future payments" has been unticked. Click Confirm again to continue.`
          );
        } else {
          setAddCardError(msg);
        }
      } finally {
        addCardRef.current = false;
        setIsAddingCard(false);
      }
    },
    [
      cardInput,
      stripe,
      elements,
      saveCard,
      paymentMethods.length,
      defaultBillingAddress,
      checkout,
      selectPaymentMethod,
      isMasterAccount,
      accountResolved,
      markNotSaveable,
    ]
  );

  const handleTryAgain = useCallback(() => {
    setPaymentError(null);
    setView("payment");
  }, []);

  const handleChangePaymentMethod = useCallback(() => {
    setView("selector");
  }, []);

  const dismissToast = useCallback(() => {
    setToastMessage(null);
  }, []);

  // Determine the header title based on current view
  const headerTitle = view === "selector" ? "Credit Cards" : view === "addCard" ? "Add card" : "Payment";

  // ── Render: Processing ───────────────────────────────────────────────────
  if (view === "processing") {
    return (
      <div ref={rootRef} className={CONTENT_ROOT}>
        <div className={HEADER}>
          <h2 className={HEADER_TITLE} data-cypress-id={`paymentTitle`}>
            Payment
          </h2>
          <button type="button" className={CLOSE_BTN} onClick={onClose} data-cypress-id={`closeIconOnPaymentPage`}>
            <CloseIcon />
          </button>
        </div>
        <div className={CENTER_STATE}>
          <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-border border-t-[#ef2b55]" />
          <h3 className={CENTER_TITLE}>{processingTitle}</h3>
          <p className={CENTER_DESC}>{processingDescription}</p>
        </div>
      </div>
    );
  }

  // ── Render: 3DS Pending ──────────────────────────────────────────────────
  if (view === "threeds") {
    return (
      <div ref={rootRef} className={CONTENT_ROOT}>
        <div className={HEADER}>
          <h2 className={HEADER_TITLE}>Payment</h2>
          <button type="button" className={CLOSE_BTN} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div className={CENTER_STATE}>
          <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-border border-t-[#ef2b55]" />
          <h3 className={CENTER_TITLE}>Approve the payment to continue</h3>
          <p className={CENTER_DESC}>
            Please complete the authentication step in your card provider's window to confirm the payment.
          </p>
        </div>
      </div>
    );
  }

  // ── Render: Failed ───────────────────────────────────────────────────────
  if (view === "failed") {
    return (
      <div ref={rootRef} className={CONTENT_ROOT}>
        <div className={HEADER}>
          <h2 className={HEADER_TITLE}>Payment</h2>
          <button type="button" className={CLOSE_BTN} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div className={CENTER_STATE}>
          <div className={CENTER_ICON}>
            <FailedCircleIcon />
          </div>
          <h3 className={CENTER_TITLE}>Payment failed!</h3>
          <p className={CENTER_DESC}>
            {paymentError || "Your payment couldn't be processed.\nPlease check your details or try another method."}
          </p>
          <div className={CENTER_BUTTONS}>
            <Button type="button" variant="outline" className="box-border h-10 px-6" onClick={handleTryAgain}>
              Try again
            </Button>
            <Button type="button" className="box-border h-10 px-6" onClick={handleChangePaymentMethod}>
              Change payment method
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Success ──────────────────────────────────────────────────────
  if (view === "success") {
    return (
      <div ref={rootRef} className={CONTENT_ROOT}>
        <div className={HEADER}>
          <h2 className={HEADER_TITLE} data-cypress-id={`paymentTitle`}>
            Payment
          </h2>
          {/* No close button — success is terminal. The client is redirected to
              their campaign list and must not be able to reopen the paid
              campaign and pay again (PAD-31453). */}
        </div>
        <div className={CENTER_STATE}>
          <div className={CENTER_ICON}>
            <SuccessCircleIcon />
          </div>
          <h3 className={CENTER_TITLE}>Payment successful!</h3>
          <p className={CENTER_DESC}>
            {
              "Your campaign has been submitted for review.\nA paid invoice will soon be available on your Billing page."
            }
          </p>
          <div
            className="text-muted-foreground flex items-center justify-center gap-2 text-sm font-medium"
            data-cypress-id={`redirectingDisclaimer`}
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            Taking you to your dashboard…
          </div>
          {/* Manual fallback in case the auto-redirect doesn't fire (blocked
              navigation, etc.). Forward-only — it can't return the client to the
              paid campaign. */}
          <div className={CENTER_BUTTONS}>
            <Button
              type="button"
              className="box-border h-10 px-6"
              onClick={() => onPaymentSuccess?.()}
              data-cypress-id={`goToCampaignsBtn`}
            >
              Go to my dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Card Selector ────────────────────────────────────────────────
  if (view === "selector") {
    return (
      <div ref={rootRef} className={CONTENT_ROOT}>
        <div className={HEADER}>
          <div className="flex items-center gap-2">
            <button type="button" className={BACK_BTN} onClick={handleBackToPayment}>
              <ArrowLeftIcon />
            </button>
            <h2 className={HEADER_TITLE}>{headerTitle}</h2>
          </div>
          <button type="button" className={CLOSE_BTN} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <SavedCardSelector
          paymentMethods={paymentMethods}
          selectedId={selectedPaymentMethodId}
          onSelect={handleSelectCard}
          onAddCard={handleAddCardFromSelector}
          canAddMore={canAddMore}
        />

        <DrawerFooter />
      </div>
    );
  }

  // ── Render: Add Card Form ────────────────────────────────────────────────
  if (view === "addCard") {
    return (
      <div ref={rootRef} className={CONTENT_ROOT}>
        <div className={HEADER}>
          <div className="flex items-center gap-2">
            <button type="button" className={BACK_BTN} onClick={handleBackToPayment}>
              <ArrowLeftIcon />
            </button>
            <h2 className={HEADER_TITLE}>{headerTitle}</h2>
          </div>
          <button type="button" className={CLOSE_BTN} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <NewCardForm
          cardInput={cardInput}
          saveCard={saveCard}
          onSaveCardChange={setSaveCard}
          saveCardBlockedReason={saveCardBlockedReason}
          isCheckingFunding={cardFunding.isCheckingFunding}
          hasDefaultAddress={!!defaultBillingAddress}
          onConfirm={handleAddCardConfirm}
          defaultCountryCode={countryCode}
        />

        {addCardError && <div className={ERROR_BANNER}>{addCardError}</div>}
        {addCardNotice && (
          <div className={NOTICE_BANNER} role="status" data-cypress-id="saveCardNotice">
            {addCardNotice}
          </div>
        )}
        {isAddingCard && <div className={LOADING_TEXT}>{saveCard ? "Saving card..." : "Adding card..."}</div>}

        <DrawerFooter />
      </div>
    );
  }

  // ── Render: Main Payment Drawer ──────────────────────────────────────────
  return (
    <div ref={rootRef} className="flex h-full min-h-screen flex-col gap-4 overflow-y-auto p-8">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-foreground m-0 text-2xl font-bold leading-8">{headerTitle}</h2>
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground flex items-center justify-center p-1"
          onClick={onClose}
        >
          <CloseIcon />
        </button>
      </div>

      {billingGate.renderBanner("checkout", { variant: "checkout" })}

      {/* Payment card */}
      <div className="flex flex-col gap-4 rounded-xl border border-solid border-border bg-background p-6">
        {/* Total + Confirm */}
        <div>
          <div
            className="text-muted-foreground m-0 text-base font-medium leading-5"
            data-cypress-id={`totalTitleInSummary`}
          >
            Total
          </div>
          <div className="flex items-center gap-2">
            <span
              className="text-foreground flex-1 text-4xl font-semibold leading-[48px]"
              data-cypress-id={`totalAmountValueInSummary`}
            >
              {breakdown ? formatCurrency(breakdown.total, breakdown.currency) : <Skeleton className="h-9 w-28" />}
            </span>
            <Button
              type="button"
              className="box-border h-11 rounded-xl px-6"
              disabled={isLoading || !hasSelectedCard || !breakdownReady || !breakdown || billingBlocked}
              onClick={handleConfirmPayment}
              data-cypress-id={`confirmPaymentBtn`}
            >
              Confirm Payment
            </Button>
          </div>
        </div>

        {/* Card + Breakdown wrapper (no gap — each section handles its own border/padding) */}
        <div>
          {/* Card Section */}
          {isLoading ? (
            <div className={LOADING_TEXT}>Loading payment methods...</div>
          ) : selectedCard ? (
            <div className="border-t border-solid border-border py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden">
                    <CardBrandIcon brand={toCardBrand(selectedCard.card.brand)} />
                  </div>
                  <span className="text-foreground text-base font-semibold leading-6">
                    {selectedCard.card.brand.toUpperCase()} •••• {selectedCard.card.last4}
                  </span>
                  {selectedCard.isExpired && (
                    <span className="text-destructive bg-destructive/10 ml-2 rounded px-2 py-0.5 text-xs font-semibold">
                      Expired
                    </span>
                  )}
                </div>
                <button type="button" className={CHANGE_CARD_BTN} onClick={handleChangeCard}>
                  Change card
                  <span className="flex h-6 w-6 items-center justify-center">
                    <ChevronRightIcon />
                  </span>
                </button>
              </div>
            </div>
          ) : pendingCard ? (
            <div className="border-t border-solid border-border py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden">
                    <CardBrandIcon brand={toCardBrand(pendingCard.brand)} />
                  </div>
                  <span className="text-foreground text-base font-semibold leading-6">
                    {pendingCard.brand.toUpperCase()} •••• {pendingCard.last4}
                  </span>
                </div>
                <button type="button" className={CHANGE_CARD_BTN} onClick={handleChangeCard}>
                  Change card
                  <span className="flex h-6 w-6 items-center justify-center">
                    <ChevronRightIcon />
                  </span>
                </button>
              </div>
            </div>
          ) : (
            <div className="border-t border-solid border-border py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden">
                    <StripeCardPlaceholder />
                  </div>
                  <span
                    className="text-muted-foreground text-base font-semibold leading-6"
                    data-cypress-id={`addCreditCardTitle`}
                  >
                    Add a credit card first
                  </span>
                </div>
                <button
                  type="button"
                  className={CHANGE_CARD_BTN}
                  onClick={() => setView(hasCards ? "selector" : "addCard")}
                  data-cypress-id={`addCardBtn`}
                >
                  Add card
                  <span className="flex h-6 w-6 items-center justify-center">
                    <ChevronRightIcon />
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Cost Breakdown + Total Due */}
          <CheckoutSummary breakdown={breakdown} />
        </div>

        {/* Error */}
        {error && <div className={ERROR_BANNER}>{error}</div>}
      </div>

      {/* Footer */}
      <DrawerFooter />

      {/* Disclaimer */}
      <div className="text-muted-foreground flex flex-col gap-1 text-center text-xs font-normal">
        <span>The Oppizi Team will review your campaign to ensure everything is set up properly.</span>
        {!hasSelectedCard && <span>You'll be charged only when your campaign is approved.</span>}
      </div>

      {/* Toast */}
      {toastMessage && <Toast message={toastMessage} onClose={dismissToast} />}

      {/* Inline Set-up drawer; on save the gate clears and pay unblocks. */}
      {billingGate.drawer}
    </div>
  );
};

// Wrapper that provides Stripe Elements context
const CheckoutContent: React.FC<CheckoutContentProps> = (props) => {
  const { stripePromise, error: stripeKeyError } = useCountryStripe(props.countryCode);

  // Side effect (logging) is kept in `useEffect` rather than the render
  // body — Strict Mode and React's concurrent rendering can invoke the
  // function component multiple times per real change, which would
  // otherwise duplicate the log noise.
  useEffect(() => {
    if (stripeKeyError) {
      // eslint-disable-next-line no-console
      console.error("[CheckoutContent] Stripe key error:", stripeKeyError.message);
    }
  }, [stripeKeyError]);

  // A missing country at checkout-open time used to be
  // silently mapped to the US publishable key; the BE created the PI
  // on the client's actual Stripe account (e.g. AU) and `stripe.js`
  // then queried the US account → 404 "No such payment_intent". Three
  // render states keep us safe:
  //
  //   - `stripeKeyError` set → the per-country publishable key is
  //     unknown or missing on the deployed site. Surface a banner so
  //     the user has signal and ops has a fingerprint to chase,
  //     instead of mounting `<Elements>` with the wrong key.
  //   - `stripePromise` null → country still loading. Hold the
  //     checkout in a non-interactive state.
  //   - otherwise → mount Stripe Elements with the correct key.
  if (stripeKeyError) {
    return (
      <div role="alert" style={{ padding: 24, color: "#b00020" }}>
        Checkout is currently unavailable for this account. Please contact support.
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div role="status" aria-live="polite" style={{ padding: 24 }}>
        Loading checkout…
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <CheckoutContentInner {...props} />
    </Elements>
  );
};

export default CheckoutContent;
